import { Icon, Tooltip, message } from "antd";
import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import SettingsVirtualTable from "../components/SettingsVirtualTable";
import { isPaymentRequired } from '~/helpers/isPaymentRequired';

var DEPT_FILTERS = [
  { value: 1, label: 'Legalização',            color: '#722ed1', bg: '#f9f0ff' },
  { value: 2, label: 'Administrativo',         color: '#1890ff', bg: '#e6f7ff' },
  { value: 3, label: 'Contábil',               color: '#13c2c2', bg: '#e6fffb' },
  { value: 4, label: 'Fiscal',                 color: '#52c41a', bg: '#f6ffed' },
  { value: 5, label: 'Pessoal',                color: '#fa8c16', bg: '#fff7e6' },
  { value: 6, label: 'Sucesso do Cliente (CS)', color: '#eb2f96', bg: '#fff0f6' },
];

var TYPE_FILTERS = [
  { value: 'payable',       label: 'Guias para Pagamento',   color: '#1890ff', bg: '#e6f7ff' },
  { value: 'informational', label: 'Documentos Informativos', color: '#52c41a', bg: '#f6ffed' },
];

function pillStyle(isActive, color, bg) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 14px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: isActive ? 600 : 400,
    cursor: 'pointer',
    transition: 'all 0.15s',
    border: '1px solid ' + (isActive ? color : '#e8e8e8'),
    background: isActive ? bg : '#fff',
    color: isActive ? color : '#595959',
  };
}

function deptCode(value) {
  var n = parseInt(value, 10);
  return isNaN(n) ? null : n;
}

function getDeptMeta(code) {
  var n = deptCode(code);
  return DEPT_FILTERS.find(function(x) { return x.value === n; }) || { label: '—', color: '#8c8c8c', bg: '#fafafa' };
}

export var DocumentTypesMatrix = function(props) {
  var searchTerm = props.searchTerm || '';
  var onOpenTask = props.onOpenTask;

  var deptFilterState = useState(null);
  var deptFilter = deptFilterState[0];
  var setDeptFilter = deptFilterState[1];

  var typeFilterState = useState(null);
  var typeFilter = typeFilterState[0];
  var setTypeFilter = typeFilterState[1];

  var tasks = useSelector(function(state) { return state.tasks.list; });
  var dispatch = useDispatch();

  var savingState = useState({});
  var saving = savingState[0];
  var setSaving = savingState[1];

  var handleToggleTipo = function(record) {
    var rowKey = record.id;
    if (saving[rowKey]) return;

    var startSaving = {};
    startSaving[rowKey] = true;
    setSaving(Object.assign({}, saving, startSaving));

    var nextValue = !isPaymentRequired(record.requires_payment);
    var body = {
      id: record.doc_id,
      index: record.doc_index,
      requires_payment: nextValue,
    };

    dispatch({
      type: 'SAVE_TASK',
      payload: {
        request: {
          method: 'patch',
          url: '/tasks/' + record.task_id + '/document-types/requires-payment',
          data: body
        }
      }
    }).then(function() {
      message.success('Tipo atualizado com sucesso');
    }).catch(function() {
      message.error('Não foi possível atualizar o tipo');
    }).finally(function() {
      setSaving(function(prev) {
        var next = Object.assign({}, prev);
        delete next[rowKey];
        return next;
      });
    });
  };

  // Flatten: for each task with file_upload, expand each entry of document_types into a row
  var rows = React.useMemo(function() {
    var out = [];
    (tasks || []).forEach(function(t) {
      if (!t || !Array.isArray(t.document_types)) return;
      t.document_types.forEach(function(doc, idx) {
        out.push({
          id: t.id + '__' + (doc && doc.id ? doc.id : idx),
          task_id: t.id,
          task_name: t.name,
          department: deptCode(t.department),
          description: (doc && doc.description) || '(sem nome)',
          requires_payment: doc && doc.requires_payment,
          doc_id: doc && doc.id ? doc.id : null,
          doc_index: idx,
        });
      });
    });
    return out;
  }, [tasks]);

  // Counts always reflect the full dataset, not filtered
  var deptCounts = React.useMemo(function() {
    var counts = { all: rows.length };
    DEPT_FILTERS.forEach(function(d) { counts[d.value] = 0; });
    rows.forEach(function(r) {
      if (counts[r.department] !== undefined) counts[r.department]++;
    });
    return counts;
  }, [rows]);

  var typeCounts = React.useMemo(function() {
    var payable = 0;
    var informational = 0;
    rows.forEach(function(r) {
      if (isPaymentRequired(r.requires_payment)) payable++;
      else informational++;
    });
    return { all: payable + informational, payable: payable, informational: informational };
  }, [rows]);

  // Apply chip filters
  var filtered = rows.filter(function(r) {
    if (deptFilter !== null && r.department !== deptFilter) return false;
    if (typeFilter !== null) {
      var isPay = isPaymentRequired(r.requires_payment);
      if (typeFilter === 'payable' && !isPay) return false;
      if (typeFilter === 'informational' && isPay) return false;
    }
    return true;
  }).sort(function(a, b) {
    if (a.department !== b.department) return (a.department || 0) - (b.department || 0);
    var byTask = (a.task_name || '').localeCompare(b.task_name || '');
    if (byTask !== 0) return byTask;
    return (a.description || '').localeCompare(b.description || '');
  });

  var columns = [
    {
      key: 'description', title: 'Documento',
      render: function(record) {
        return React.createElement('span', {
          style: { display: 'inline-flex', alignItems: 'center', gap: 6 }
        },
          React.createElement(Icon, { type: 'file-text', style: { color: '#52c41a', fontSize: 13 } }),
          record.description
        );
      }
    },
    {
      key: 'task_name', title: 'Tarefa', width: 280,
      render: function(record) { return record.task_name; }
    },
    {
      key: 'department', title: 'Departamento', width: 220,
      render: function(record) {
        var meta = getDeptMeta(record.department);
        return React.createElement('span', {
          style: {
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 500,
            background: meta.bg, color: meta.color,
            border: '1px solid ' + meta.color + '33',
          }
        },
          React.createElement('span', {
            style: { width: 7, height: 7, borderRadius: '50%', background: meta.color }
          }),
          meta.label
        );
      },
      filterValue: function(record) { return String(record.department); },
      filters: DEPT_FILTERS.map(function(d) { return { value: String(d.value), label: d.label }; })
    },
    {
      key: 'requires_payment', title: 'Tipo', width: 200,
      render: function(record) {
        var isRequired = isPaymentRequired(record.requires_payment);
        var isSaving = !!saving[record.id];
        return React.createElement(Tooltip, {
          title: isSaving ? 'Salvando...' : ('Clique para alterar para ' + (isRequired ? 'Informativo' : 'Pagável'))
        },
          React.createElement('span', {
            onClick: function(e) {
              e.stopPropagation();
              if (!isSaving) handleToggleTipo(record);
            },
            style: {
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 12px', borderRadius: 14, cursor: isSaving ? 'wait' : 'pointer',
              fontSize: 12, fontWeight: 500, opacity: isSaving ? 0.6 : 1, transition: 'all 0.15s',
              background: isRequired ? '#e6f7ff' : '#f6ffed',
              color: isRequired ? '#1890ff' : '#52c41a',
              border: '1px solid ' + (isRequired ? '#91d5ff' : '#b7eb8f'),
            }
          },
            React.createElement(Icon, {
              type: isSaving ? 'loading' : (isRequired ? 'dollar' : 'file-text'),
              style: { fontSize: 12 }
            }),
            isRequired ? 'Pagável' : 'Informativo',
            React.createElement(Icon, {
              type: 'swap',
              style: { fontSize: 10, opacity: 0.5 }
            })
          )
        );
      },
      filterValue: function(record) { return isPaymentRequired(record.requires_payment) ? 'true' : 'false'; },
      filters: [
        { value: 'true', label: 'Pagável' },
        { value: 'false', label: 'Informativo' },
      ]
    }
  ];

  var deptPillsEl = React.createElement('div', {
    style: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }
  },
    React.createElement('span', {
      style: pillStyle(deptFilter === null, '#1890ff', '#e6f7ff'),
      onClick: function() { setDeptFilter(null); }
    },
      'Todos',
      React.createElement('span', { style: { fontSize: 11, fontWeight: 600, opacity: 0.7 } }, deptCounts['all'])
    ),
    DEPT_FILTERS.map(function(f) {
      var isActive = deptFilter === f.value;
      return React.createElement('span', {
        key: f.value,
        style: pillStyle(isActive, f.color, f.bg),
        onClick: function() { setDeptFilter(isActive ? null : f.value); }
      },
        React.createElement('span', {
          style: { width: 8, height: 8, borderRadius: '50%', background: f.color, flexShrink: 0 }
        }),
        f.label,
        React.createElement('span', { style: { fontSize: 11, fontWeight: 600, opacity: 0.7 } }, deptCounts[f.value])
      );
    })
  );

  var typePillsEl = React.createElement('div', {
    style: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }
  },
    React.createElement('span', {
      style: pillStyle(typeFilter === null, '#1890ff', '#e6f7ff'),
      onClick: function() { setTypeFilter(null); }
    },
      'Todos os tipos',
      React.createElement('span', { style: { fontSize: 11, fontWeight: 600, opacity: 0.7 } }, typeCounts.all)
    ),
    TYPE_FILTERS.map(function(f) {
      var isActive = typeFilter === f.value;
      var count = f.value === 'payable' ? typeCounts.payable : typeCounts.informational;
      return React.createElement('span', {
        key: f.value,
        style: pillStyle(isActive, f.color, f.bg),
        onClick: function() { setTypeFilter(isActive ? null : f.value); }
      },
        React.createElement(Icon, {
          type: f.value === 'payable' ? 'dollar' : 'file-text',
          style: { fontSize: 12 }
        }),
        f.label,
        React.createElement('span', { style: { fontSize: 11, fontWeight: 600, opacity: 0.7 } }, count)
      );
    })
  );

  var handleRowClick = function(record) {
    if (typeof onOpenTask !== 'function') return;
    var task = (tasks || []).find(function(t) { return t.id === record.task_id; });
    if (task) onOpenTask(task);
  };

  return (
    <div>
      {deptPillsEl}
      {typePillsEl}
      <SettingsVirtualTable
        data={filtered}
        columns={columns}
        searchTerm={searchTerm}
        onRowClick={handleRowClick}
      />
    </div>
  );
};

export default DocumentTypesMatrix;
